<template>
  <v-container class="join-by-link fill-height" fluid>
    <v-row align="center" justify="center" style="min-width: 80vw;">
      <v-col cols="12" sm="10" md="8" lg="6" xl="5">
        <v-card class="pa-6 elevation-8">
          <div class="text-center mb-6">
            <img src="@/assets/images/logo.svg" alt="Logo" style="max-width: 200px;" />
          </div>

          <div v-if="loading" class="text-center py-8">
            <v-progress-circular indeterminate color="primary" :size="60" :width="6" />
          </div>

          <!-- Lien régénéré, source fermée, lien désactivé : une seule réponse -->
          <div v-else-if="unavailable" class="text-center py-8">
            <v-icon size="80" color="error" class="mb-4">mdi-link-off</v-icon>
            <h2 class="text-h6 mb-4">{{ $t('onboarding.join.unavailable.title') }}</h2>
            <p class="text--secondary">{{ $t('onboarding.join.unavailable.message') }}</p>
          </div>

          <!-- Envoyé : la suite est dans la boîte -->
          <div v-else-if="sentTo" class="text-center py-6">
            <v-icon size="80" color="primary" class="mb-4">mdi-email-check-outline</v-icon>
            <h2 class="text-h6 mb-3">{{ $t('onboarding.join.sent.title') }}</h2>
            <p class="text--secondary">{{ $t('onboarding.join.sent.message', { email: sentTo }) }}</p>
            <v-btn text small class="mt-4" @click="sentTo = null">{{ $t('onboarding.join.sent.other-address') }}</v-btn>
          </div>

          <template v-else>
            <div class="text-center">
              <v-icon size="72" color="primary" class="mb-4">mdi-account-plus-outline</v-icon>
              <h1 class="text-h5 mb-3">{{ $t('onboarding.tunnel.welcome.title', { organization: page.organization.name }) }}</h1>
              <p class="text--secondary mb-4">{{ $t('onboarding.tunnel.welcome.message') }}</p>
            </div>

            <v-list dense class="mx-auto mb-2" style="max-width: 420px;">
              <v-list-item v-for="(name, index) in page.steps" :key="name">
                <v-list-item-avatar size="28" color="primary" class="white--text">{{ index + 1 }}</v-list-item-avatar>
                <v-list-item-content>{{ $t(`onboarding.tunnel.step.${name === 'deposit' && page.approval ? 'deposit-authorization' : name}`) }}</v-list-item-content>
              </v-list-item>
            </v-list>
            <p v-if="page.homeCharging && page.homeCharging !== 'none'" class="text-caption text--secondary text-center mb-2">
              {{ $t('onboarding.join.home-notice') }}
            </p>
            <p v-if="page.vehicle && page.vehicle !== 'none'" class="text-caption text--secondary text-center mb-2">
              {{ $t(`onboarding.join.vehicle-notice.${page.vehicle}`) }}
            </p>
            <p v-if="page.approval" class="text-caption text--secondary text-center mb-6">
              {{ $t('onboarding.join.approval-notice', { organization: page.organization.name }) }}
            </p>

            <v-alert v-if="page.full" type="info" dense text class="mb-4">{{ $t('onboarding.join.full') }}</v-alert>
            <v-alert v-if="error" type="error" dense outlined class="mb-4">{{ error }}</v-alert>

            <v-form ref="form" v-model="valid" class="mx-auto" style="max-width: 420px;" @submit.prevent="request">
              <p class="mb-2">{{ $t('onboarding.join.email-explained') }}</p>
              <v-text-field
                  v-model="email"
                  type="email"
                  :label="$t('email')"
                  :rules="[validEmail]"
                  outlined dense autofocus
              />
              <v-btn color="primary" block small type="submit" :loading="sending" :disabled="!valid">
                {{ $t('onboarding.join.send') }}
              </v-btn>
            </v-form>
          </template>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import RegistrationRepository from '@repository/RegistrationRepository'

const LOCALES = ['fr', 'en', 'nl']

/**
 * La page d'un lien d'invitation : qui invite, ce qui sera demandé, et l'adresse à laquelle
 * envoyer de quoi continuer. Rien n'est créé ici — le dossier naît quand la personne clique dans
 * l'e-mail, ce qui prouve que l'adresse est à elle. La réponse est la même qu'elle ait déjà un
 * dossier ou non.
 */
export default {
  name: 'JoinByLink',

  data() {
    return { loading: true, unavailable: false, page: null, email: '', valid: false, sending: false, sentTo: null, error: null }
  },

  computed: {
    token() {
      return this.$route.params.token || ''
    },
  },

  created() {
    if (LOCALES.includes(this.$route.query.locale)) {
      this.$i18n.locale = this.$route.query.locale
    }
    this.load()
  },

  methods: {
    validEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim()) || this.$t('onboarding.join.invalid-email')
    },

    async load() {
      try {
        this.page = await RegistrationRepository.joinPage(this.token)
      } catch (e) {
        this.unavailable = true
      } finally {
        this.loading = false
      }
    },

    async request() {
      if (!this.$refs.form.validate()) return
      this.sending = true
      this.error = null
      try {
        await RegistrationRepository.requestJoin(this.token, this.email.trim(), this.$i18n.locale)
        this.sentTo = this.email.trim()
      } catch (e) {
        const data = e?.response?.data
        const key = data?.messageKey
        if (key === 'onboarding.join.link-unavailable') {
          this.unavailable = true
          return
        }
        this.error = e?.response?.status === 429
          ? this.$t('onboarding.join.too-many')
          : (key && this.$te(key) ? this.$t(key, data.parameters || {}) : this.$t('error-occurred'))
      } finally {
        this.sending = false
      }
    },
  },
}
</script>
